import { useEffect, useMemo, useState } from 'react'
import { isConfigured, watchAuth, completeRedirect, endSession } from './firebase.js'
import { onForegroundMessage } from './push.js'
import { useWingfinderData } from './useWingfinderData.js'
import {
  groupListings, groupByDealer, applyViewFilter, isUnread,
  noteFor, dealerKey, EMPTY_FILTER,
} from './util.js'
import { saveNote, saveDealer, saveCriteria, markAlertSeen, markAllAlertsSeen } from './workspace.js'
import ListingCard from './components/ListingCard.jsx'
import ListingTable from './components/ListingTable.jsx'
import ListingDetail from './components/ListingDetail.jsx'
import DealerGroup from './components/DealerGroup.jsx'
import FilterBar from './components/FilterBar.jsx'
import CriteriaEditor from './components/CriteriaEditor.jsx'
import Inbox from './components/Inbox.jsx'
import StatusBar from './components/StatusBar.jsx'
import PushBanner from './components/PushBanner.jsx'
import SignIn from './components/SignIn.jsx'
import AccessGate from './components/AccessGate.jsx'

const FILTER_KEY = 'wingfinder.viewFilter'

const loadFilter = () => {
  try {
    return { ...EMPTY_FILTER, ...JSON.parse(localStorage.getItem(FILTER_KEY) || '{}') }
  } catch {
    return EMPTY_FILTER
  }
}

function Section({ title, hint, listings, notes, defaultOpen = true, dense = false, onOpen, onStatus }) {
  const [open, setOpen] = useState(defaultOpen)
  if (listings.length === 0) return null
  return (
    <section>
      {/* The header is a button, so its content model permits phrasing content
          only — no h2 nested inside it. Heading semantics come from
          role="heading" on a span instead, same pattern as CriteriaEditor
          and DealerGroup's panel headers. */}
      <button className="section-head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span role="heading" aria-level="2">{title} <span className="count">{listings.length}</span></span>
        <span className="chev">{open ? '▾' : '▸'}</span>
      </button>
      {hint && open && <p className="hint">{hint}</p>}
      {open && (dense ? (
        <ListingTable listings={listings} notes={notes} onOpen={onOpen} onStatus={onStatus} />
      ) : (
        <div className="grid">
          {listings.map((l) => (
            <ListingCard
              key={l.id}
              listing={l}
              note={noteFor(notes, l.id)}
              onOpen={onOpen}
              onStatus={onStatus}
            />
          ))}
        </div>
      ))}
    </section>
  )
}

export default function App() {
  const [auth, setAuth] = useState({ loading: true, user: null, error: null })
  const [toast, setToast] = useState(null)
  const [view, setView] = useState('tier')
  const [filter, setFilter] = useState(loadFilter)
  const [openId, setOpenId] = useState(null)

  const data = useWingfinderData(auth.user)

  // Every toast — a push, a save result — clears itself after the same
  // interval, so a "Could not save" from a minute ago can't be mistaken for
  // a current failure once the operator has moved on.
  const showToast = (msg) => {
    setToast(msg)
    setTimeout(() => setToast(null), 6000)
  }

  useEffect(() => {
    if (!isConfigured) return
    let stop = () => {}
    completeRedirect().then((redirectError) => {
      stop = watchAuth(
        (user) => setAuth({ loading: false, user, error: redirectError?.message ?? null }),
        (err) => setAuth({ loading: false, user: null, error: err.message })
      )
    })
    return () => stop()
  }, [])

  useEffect(() => {
    let stop = () => {}
    onForegroundMessage((payload) => {
      showToast(payload.notification?.title || 'New activity')
    }).then((fn) => (stop = fn || (() => {})))
    return () => stop()
  }, [])

  useEffect(() => {
    try { localStorage.setItem(FILTER_KEY, JSON.stringify(filter)) } catch { /* private mode */ }
  }, [filter])

  // A push carries the listing id; open that bike directly rather than dropping
  // the operator into the full list with no idea which one fired. Gated on
  // auth.user: on mount, auth has not resolved yet (completeRedirect/watchAuth
  // above settle asynchronously), so firing this before that would send the
  // write out unauthenticated, where the rules deny it and — before this fix —
  // the denial was thrown away with an empty .catch(), unlike every other
  // write in this file.
  useEffect(() => {
    if (!auth.user) return
    const id = new URLSearchParams(window.location.search).get('listing')
    if (!id) return
    setOpenId(id)
    markAlertSeen(id).catch((e) => showToast(`Could not save: ${e.message}`))
    window.history.replaceState({}, '', window.location.pathname)
  }, [auth.user])

  // The other half of push click-through. The Firebase SDK's own
  // notificationclick listener opens or focuses a window for the link — but on
  // an ALREADY-OPEN window it only .focus()es without navigating, and posts a
  // NOTIFICATION_CLICKED message instead. Nothing listened for it, so tapping
  // an alert with the app open left the operator on whatever bike was already
  // on screen. A custom notificationclick listener in the service worker
  // cannot fix this — see the comment in public/firebase-messaging-sw.js.
  useEffect(() => {
    if (!auth.user || !navigator.serviceWorker) return

    const onMessage = (event) => {
      if (event.data?.messageType !== 'notification-clicked') return
      // The SDK posts the raw push payload here, not a wrapper of its own —
      // decompiled from the firebase-messaging-compat.js bundle. Two
      // different Firebase versions are in play in this app (see the note in
      // public/firebase-messaging-sw.js): this file (the app bundle) gets
      // whatever `firebase@^12.3.0` in package.json resolves to via npm
      // (12.17.0 as of this fix), while the service worker below loads
      // 12.3.0 itself, pinned directly off the gstatic CDN. The behaviour
      // this comment describes was verified against both. notify.js sends
      // `webpush.fcmOptions.link` and no top-level `data` at all, so
      // `event.data.data.link` (the original version of this line) is always
      // undefined against a real message; this mirrors the SDK's own
      // resolution order instead of guessing a shape.
      const link = event.data?.fcmOptions?.link ?? event.data?.notification?.click_action
      if (!link) return

      let id = null
      try {
        const url = new URL(link, window.location.origin)
        if (url.origin !== window.location.origin) return // not a link we constructed
        id = url.searchParams.get('listing')
      } catch {
        return // a link we did not construct; nothing to open
      }
      if (!id) return

      setOpenId(id)
      markAlertSeen(id).catch((e) => showToast(`Could not save: ${e.message}`))
    }

    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [auth.user])

  const all = useMemo(() => Object.values(data.listings), [data.listings])
  const visible = useMemo(
    () => applyViewFilter(all, data.notes, filter),
    [all, data.notes, filter]
  )
  const visibleMap = useMemo(
    () => Object.fromEntries(visible.map((l) => [l.id, l])),
    [visible]
  )
  const g = useMemo(() => groupListings(visibleMap, data.notes), [visibleMap, data.notes])
  // Unfiltered, deliberately — StatusBar exists to tell "no Gold Wings
  // matched today" apart from "the crawler has been broken for a week", and
  // a view filter (persisted in localStorage, so it can outlive the session
  // that set it) narrowing the screen to nothing must not be able to recreate
  // that exact ambiguity by driving the headline count to zero.
  const gAll = useMemo(() => groupListings(data.listings, data.notes), [data.listings, data.notes])
  const dealerGroups = useMemo(() => groupByDealer(visible, data.notes), [visible, data.notes])
  // Unfiltered too, and for the same reason as gAll above — a price-drop
  // alert on a bike being negotiated is the most valuable alert this system
  // sends, and a leftover view filter must not be able to hide it from the
  // one place that exists to surface it. "Mark all reviewed" inherits this
  // for free: it always marks everything Inbox was given, so once Inbox is
  // fed the unfiltered set the button means what it says.
  const unread = useMemo(
    () => all.filter((l) => isUnread(l, noteFor(data.notes, l.id))),
    [all, data.notes]
  )

  // Sections, the inbox and the detail sheet all read /notes (and the detail
  // sheet reads /dealers too) to decide what to show and what a save should
  // write. A first-read failure on either leaves that node at its default
  // `{}` — indistinguishable, to code that doesn't check, from "the operator
  // genuinely recorded nothing" — so every status vanishes, dismissed bikes
  // reappear, alerted bikes read unread, and the detail sheet would seed its
  // drafts from an empty note and go on to null out the operator's actual
  // data on save. A LATER refresh failure is not the same danger — the hook
  // keeps the last-known-good value in that case — so this only withholds on
  // a path that has never once loaded.
  const workspaceReady = Boolean(
    data.readyPaths?.listings && data.readyPaths?.notes && data.readyPaths?.dealers
  )

  const openListing = openId ? data.listings[openId] : null

  const onOpen = (listing) => {
    setOpenId(listing.id)
    if (isUnread(listing, noteFor(data.notes, listing.id))) {
      markAlertSeen(listing.id).catch((e) => showToast(`Could not save: ${e.message}`))
    }
  }
  const onStatus = (listing, status) =>
    saveNote(listing.id, { status }).catch((e) => showToast(`Could not save: ${e.message}`))

  if (!isConfigured) {
    return (
      <main className="setup">
        <h1>Wingfinder</h1>
        <p>Firebase isn't configured for this build.</p>
        <p className="muted">
          Copy <code>ui/.env.example</code> to <code>ui/.env.local</code>, fill in the values from
          the Firebase project, then restart the dev server.
        </p>
      </main>
    )
  }

  if (auth.loading) return <main className="setup"><p className="muted">Loading…</p></main>
  if (!auth.user) return <SignIn error={auth.error} />
  if (data.denied) return <AccessGate user={auth.user} />

  return (
    <main>
      <header>
        <h1>Wingfinder</h1>
        <p className="sub">Honda Gold Wing · 2018–2022 · within 150 mi of Buffalo, MN</p>
        <button className="signout" onClick={() => endSession()}>
          Sign out {auth.user.email}
        </button>
      </header>

      <StatusBar
        meta={data.meta}
        counts={{ matches: gAll.matches.length, soft: gAll.soft.length, tracked: gAll.tracked.length }}
      />
      <PushBanner />
      <CriteriaEditor
        // /criteria historically only ever held {priceMax, updatedAt} — the
        // other six whitelisted fields were never written by any operator
        // action, and the seed transaction in criteria.js cannot backfill
        // them once the node is non-null. Layering the saved values over
        // run.js's own meta.criteria (the effective seven-plus-origin it
        // computes and writes every run) means the form shows what is
        // actually in force — origin included, harmlessly: CRITERIA_FIELDS
        // only reads the seven named fields back out, so the extra key is
        // ignored. data.criteria on the right wins per field so an explicit
        // operator save (including an explicit null clearing a field, see
        // saveCriteria) still overrides the computed default.
        criteria={{ ...data.meta?.criteria, ...data.criteria }}
        onSave={(v) =>
          saveCriteria(v).catch((e) => {
            showToast(`Could not save: ${e.message}`)
            // Re-thrown so CriteriaEditor's own await sees the rejection —
            // swallowing it here would let its unconditional setSaved(true)
            // report success on a write that never happened.
            throw e
          })
        }
      />
      <FilterBar filter={filter} onChange={setFilter} shown={visible.length} total={all.length} />

      <div className="viewtoggle" role="group" aria-label="Group by">
        <button className={view === 'tier' ? 'on' : ''} onClick={() => setView('tier')}>Match tier</button>
        <button className={view === 'dealer' ? 'on' : ''} onClick={() => setView('dealer')}>Dealer</button>
      </div>

      {data.error && <p className="err block">Could not read {data.errorPath || 'listings'}: {data.error}</p>}
      {data.loading && !data.error && <p className="muted block">Loading…</p>}

      {!data.loading && workspaceReady && all.length === 0 && !data.error && (
        <p className="muted block">
          No listings stored yet. The crawler writes here after its first run.
        </p>
      )}

      {!data.loading && workspaceReady && all.length > 0 && visible.length === 0 && (
        <p className="muted block">Nothing matches this filter. Clear it to see all {all.length}.</p>
      )}

      {workspaceReady && (
        <Inbox
          listings={unread}
          notes={data.notes}
          onOpen={onOpen}
          onStatus={onStatus}
          onMarkAll={(ids) => markAllAlertsSeen(ids).catch((e) => showToast(`Could not save: ${e.message}`))}
        />
      )}

      {workspaceReady && (view === 'tier' ? (
        <>
          <Section title="Working" listings={g.working} notes={data.notes} onOpen={onOpen} onStatus={onStatus} />
          <Section title="Interested" listings={g.interested} notes={data.notes} onOpen={onOpen} onStatus={onStatus} />
          <Section title="Matches" listings={g.matches} notes={data.notes} onOpen={onOpen} onStatus={onStatus} />
          <Section title="Close" hint="Meets your criteria except where noted." listings={g.soft} notes={data.notes} onOpen={onOpen} onStatus={onStatus} />
          <Section title="Tracked" hint="Doesn't match today. Kept so a price drop can be caught later." listings={g.tracked} notes={data.notes} defaultOpen={false} dense onOpen={onOpen} onStatus={onStatus} />
          <Section title="Gone" listings={g.delisted} notes={data.notes} defaultOpen={false} dense onOpen={onOpen} onStatus={onStatus} />
          <Section title="Dismissed" hint="Sold, not found, or passed on." listings={g.dismissed} notes={data.notes} defaultOpen={false} dense onOpen={onOpen} onStatus={onStatus} />
        </>
      ) : (
        dealerGroups.map((group) => (
          <DealerGroup
            key={group.key}
            group={group}
            dealer={data.dealers[group.key] || {}}
            notes={data.notes}
            onOpen={onOpen}
            onStatus={onStatus}
          />
        ))
      ))}

      {workspaceReady && openListing && (
        <ListingDetail
          listing={openListing}
          note={noteFor(data.notes, openListing.id)}
          dealer={data.dealers[dealerKey(openListing.dealerName)] || {}}
          onClose={() => setOpenId(null)}
          onSaveNote={saveNote}
          onSaveDealer={saveDealer}
        />
      )}

      {toast && <div className="toast">{toast}</div>}
    </main>
  )
}
